import type { TimelineAnimaticSource, TimelineAudioAsset } from "@/components/shot-panel/timeline-editor/types"
import { getCsrfToken } from "@/lib/csrf"
import { getJson, sendJson } from "@/lib/json-request"
import type { TimelineDocument, TimelineSource } from "@/lib/timeline"
import type { CanvasModel, CanvasProjectStyles, CanvasReferenceItem, CanvasShotTreeEpisode } from "@/types/canvas"
import type { CastRow, LocationRow, PropRow } from "@/components/prompt-editor/mention-sources"
import type { FlowBoardRow, FlowChainRow, FlowDefaults, FlowNodeKind, FlowNodeRow } from "@/types/flow"
import type { JSONContent } from "@tiptap/core"
import type { PostProductionModelOption, PostProductionParameters, ShotFrameSource } from "@/lib/workspace-api"

function flowBase(projectId: number): string {
  return `/projects/${projectId}/workspace/flow`
}

export type FlowEdgeResult = {
  node: FlowNodeRow
  nodes: FlowNodeRow[]
}

type EdgeResponse = { node: FlowNodeRow; nodes?: FlowNodeRow[] }

function readEdgeResult(data: EdgeResponse): FlowEdgeResult {
  const nodes = data.nodes?.length ? data.nodes : [data.node]
  return { node: data.node, nodes }
}

function abortError(): DOMException {
  return new DOMException("The operation was aborted.", "AbortError")
}

/** Файл уходит через XHR: у fetch нет прогресса заливки. */
function uploadFlowNode(
  url: string,
  input: {
    kind: FlowNodeKind
    positionX: number
    positionY: number
    file: File
    title?: string
    onProgress?: (percent: number) => void
    signal?: AbortSignal
  },
): Promise<FlowEdgeResult> {
  if (input.signal?.aborted) return Promise.reject(abortError())
  return new Promise((resolve, reject) => {
    const body = new FormData()
    body.set("kind", input.kind)
    body.set("position_x", String(input.positionX))
    body.set("position_y", String(input.positionY))
    if (input.title) body.set("title", input.title)
    body.set("file", input.file)
    body.set("authenticity_token", getCsrfToken())

    const xhr = new XMLHttpRequest()
    xhr.open("POST", url)
    xhr.withCredentials = true
    xhr.setRequestHeader("X-CSRF-Token", getCsrfToken())
    xhr.setRequestHeader("Accept", "application/json")
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest")

    const fail = () => {
      let message = `HTTP ${xhr.status}`
      try {
        const data = JSON.parse(xhr.responseText) as { error?: string }
        if (data.error) message = data.error
      } catch {
        // Тело не JSON — остаётся HTTP-статус.
      }
      reject(new Error(message))
    }

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0 || !input.onProgress) return
      const percent = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
      input.onProgress(percent)
    }

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        fail()
        return
      }
      try {
        resolve(readEdgeResult(JSON.parse(xhr.responseText) as EdgeResponse))
      } catch {
        fail()
      }
    }
    xhr.onerror = () => reject(new Error(`HTTP ${xhr.status}`))
    xhr.onabort = () => reject(abortError())

    const onAbort = () => xhr.abort()
    input.signal?.addEventListener("abort", onAbort)
    xhr.addEventListener("loadend", () => input.signal?.removeEventListener("abort", onAbort))
    xhr.send(body)
  })
}

export function flowBoardHref(projectId: number, number: number): string {
  return `${flowBase(projectId)}?flow=${number}`
}

export function flowChainHref(
  projectId: number,
  chainId: number,
  returnFlow: number | string | null,
  via?: string | null,
): string {
  const params = new URLSearchParams({ chain: String(chainId) })
  if (returnFlow != null && String(returnFlow) !== "") params.set("return", String(returnFlow))
  if (via) params.set("via", via)
  return `${flowBase(projectId)}?${params.toString()}`
}

/**
 * Назад из редактора, когда в руках via следующей цепочки: родители и id текущей.
 * Текущий id с конца снимается, дальше то же правило, что у кнопки «назад».
 */
export function flowChainEditorBackHref(
  projectId: number,
  editingChainId: number,
  returnFlow: number | string | null,
  chainVia: string | null,
): string {
  const parts = (chainVia ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
  if (parts[parts.length - 1] === String(editingChainId)) parts.pop()
  const backFlow = returnFlow != null && String(returnFlow) !== "" ? String(returnFlow) : null
  return flowChainBackHref(projectId, { return_flow: backFlow, via: parts.join(",") || null })
}

/** Назад из редактора: родительская цепочка из via, иначе доска return. */
export function flowChainBackHref(
  projectId: number,
  editor: { return_flow: string | null; via: string | null },
): string {
  const parts = (editor.via ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
  const parentId = parts.pop()
  if (parentId) return flowChainHref(projectId, Number(parentId), editor.return_flow, parts.join(",") || null)
  if (editor.return_flow) return flowBoardHref(projectId, Number(editor.return_flow))
  return `/projects/${projectId}/workspace/flow`
}

export function flowNodeHref(projectId: number, flowNumber: number, nodeId: number): string {
  return `${flowBase(projectId)}?flow=${flowNumber}&node=${nodeId}`
}

/** `?node=` с доски флоу. Принимает `location.search` или Inertia `page.url`. */
export function parseFlowFocusNodeId(searchOrUrl: string): number | null {
  const query = searchOrUrl.includes("?") ? searchOrUrl.slice(searchOrUrl.indexOf("?")) : searchOrUrl
  const raw = new URLSearchParams(query).get("node")
  if (!raw || !/^\d+$/.test(raw)) return null
  const id = Number(raw)
  return id > 0 ? id : null
}

export function stripFlowFocusNodeParam(): void {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (!url.searchParams.has("node")) return
  url.searchParams.delete("node")
  const query = url.searchParams.toString()
  const next = query ? `${url.pathname}?${query}` : url.pathname
  window.history.replaceState(window.history.state, "", next)
}

export async function createFlowBoard(projectId: number): Promise<FlowBoardRow> {
  const data = await sendJson<{ flow: FlowBoardRow }>("POST", `${flowBase(projectId)}/boards`)
  return data.flow
}

export async function renameFlowBoard(projectId: number, flowId: number, title: string): Promise<FlowBoardRow> {
  const data = await sendJson<{ flow: FlowBoardRow }>("PATCH", `${flowBase(projectId)}/boards/${flowId}`, { title })
  return data.flow
}

export async function deleteFlowBoard(projectId: number, flowId: number): Promise<{ next_flow_number: number | null }> {
  return sendJson("DELETE", `${flowBase(projectId)}/boards/${flowId}`)
}

export async function updateFlowViewport(
  projectId: number,
  flowId: number,
  viewport: { x: number; y: number; zoom: number },
): Promise<void> {
  await sendJson("PATCH", `${flowBase(projectId)}/boards/${flowId}`, {
    viewport_x: viewport.x,
    viewport_y: viewport.y,
    viewport_zoom: viewport.zoom,
  })
}

export async function fetchFlowShotTree(projectId: number): Promise<CanvasShotTreeEpisode[]> {
  const data = await getJson<{ shot_tree: CanvasShotTreeEpisode[] }>(`${flowBase(projectId)}/shot_tree`)
  return data.shot_tree
}

export async function createFlowNode(
  projectId: number,
  flowId: number,
  input: {
    kind: FlowNodeKind
    positionX: number
    positionY: number
    file?: File
    /** Имя ноды. Имеет смысл вместе с файлом: ответ приходит уже с этой подписью. */
    title?: string
    source?: ShotFrameSource
    /** Какие типы библиотеки принять. Без списка сервер оставляет картинку и видео. */
    mediaExpect?: Array<"image" | "video" | "audio">
    shotId?: number
    sceneId?: number
    newShot?: boolean
    entityId?: number
    chainId?: number
    /** Процент заливки файла, 0–100. На 100% ответ сервера ещё может не прийти. */
    onProgress?: (percent: number) => void
    signal?: AbortSignal
  },
): Promise<{ node: FlowNodeRow; nodes: FlowNodeRow[] }> {
  const url = `${flowBase(projectId)}/nodes?flow_id=${flowId}`
  if (input.file) return uploadFlowNode(url, { ...input, file: input.file })
  return readEdgeResult(
    await sendJson<EdgeResponse>("POST", url, {
      kind: input.kind,
      position_x: input.positionX,
      position_y: input.positionY,
      source: input.source,
      media_expect: input.mediaExpect,
      shot_id: input.shotId,
      scene_id: input.sceneId,
      new_shot: input.newShot,
      entity_id: input.entityId,
      chain_id: input.chainId,
    }),
  )
}

export async function createFlowChain(projectId: number, flowId: number, nodeIds: number[]): Promise<FlowChainRow> {
  const data = await sendJson<{ chain: FlowChainRow }>("POST", `${flowBase(projectId)}/chains?flow_id=${flowId}`, {
    node_ids: nodeIds,
  })
  return data.chain
}

export async function createBlankFlowChain(projectId: number): Promise<FlowChainRow> {
  const data = await sendJson<{ chain: FlowChainRow }>("POST", `${flowBase(projectId)}/chains`, { blank: true })
  return data.chain
}

export async function deleteFlowChain(
  projectId: number,
  chainId: number,
): Promise<{ next_flow_number: number | null }> {
  return sendJson("DELETE", `${flowBase(projectId)}/chains/${chainId}`)
}

export async function duplicateFlowChain(projectId: number, chainId: number): Promise<FlowChainRow> {
  const data = await sendJson<{ chain: FlowChainRow }>("POST", `${flowBase(projectId)}/chains/${chainId}/duplicate`)
  return data.chain
}

export async function runFlowChain(projectId: number, nodeId: number): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>("POST", `${flowBase(projectId)}/nodes/${nodeId}/run_chain`)
  return data.node
}

export async function duplicateFlowNodes(
  projectId: number,
  flowId: number,
  nodes: { id: number; positionX: number; positionY: number }[],
): Promise<FlowNodeRow[]> {
  const data = await sendJson<{ nodes: FlowNodeRow[] }>(
    "POST",
    `${flowBase(projectId)}/nodes/duplicate?flow_id=${flowId}`,
    {
      nodes: nodes.map((node) => ({
        id: node.id,
        position_x: node.positionX,
        position_y: node.positionY,
      })),
    },
  )
  return data.nodes
}

export async function updateFlowNode(
  projectId: number,
  nodeId: number,
  patch: Partial<{
    position_x: number
    position_y: number
    display_width: number | null
    prompt_text: string
    prompt_doc: JSONContent | null
    model_id: string
    model_params: Record<string, string>
    use_project_styles: boolean
    title: string | null
    reference_items: CanvasReferenceItem[]
    slot_index: number
    source_node_id: number
    swap_with_slot: number
    chain_input: boolean
  }>,
): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>("PATCH", `${flowBase(projectId)}/nodes/${nodeId}`, patch)
  return data.node
}

export async function deleteFlowNode(projectId: number, nodeId: number): Promise<FlowNodeRow[]> {
  const data = await sendJson<{ nodes?: FlowNodeRow[] }>("DELETE", `${flowBase(projectId)}/nodes/${nodeId}`)
  return data.nodes ?? []
}

export async function restoreFlowNode(
  projectId: number,
  nodeId: number,
): Promise<{ node: FlowNodeRow; nodes: FlowNodeRow[] }> {
  const data = await sendJson<EdgeResponse>("POST", `${flowBase(projectId)}/nodes/${nodeId}/restore`)
  return { node: data.node, nodes: data.nodes ?? [data.node] }
}

export async function purgeFlowNode(projectId: number, nodeId: number): Promise<void> {
  await sendJson("POST", `${flowBase(projectId)}/nodes/${nodeId}/purge`)
}

export async function createFlowEdge(
  projectId: number,
  flowId: number,
  sourceNodeId: number,
  targetNodeId: number,
  ports?: { sourcePort: string; targetPort: string },
): Promise<FlowEdgeResult> {
  return readEdgeResult(
    await sendJson<EdgeResponse>("POST", `${flowBase(projectId)}/edges?flow_id=${flowId}`, {
      source_node_id: sourceNodeId,
      target_node_id: targetNodeId,
      source_port: ports?.sourcePort ?? "image",
      target_port: ports?.targetPort ?? "references",
    }),
  )
}

export async function deleteFlowEdge(projectId: number, edgeId: number): Promise<FlowEdgeResult> {
  return readEdgeResult(await sendJson<EdgeResponse>("DELETE", `${flowBase(projectId)}/edges/${edgeId}`))
}

export async function restoreFlowEdge(projectId: number, edgeId: number): Promise<FlowEdgeResult> {
  return readEdgeResult(await sendJson<EdgeResponse>("POST", `${flowBase(projectId)}/edges/${edgeId}/restore`))
}

export async function syncFlowShotLinks(projectId: number, nodeId: number): Promise<FlowEdgeResult> {
  return readEdgeResult(await sendJson<EdgeResponse>("POST", `${flowBase(projectId)}/nodes/${nodeId}/sync_links`))
}

export async function assignFlowKeyframe(
  projectId: number,
  nodeId: number,
  sourceNodeId: number,
): Promise<FlowEdgeResult> {
  return readEdgeResult(
    await sendJson<EdgeResponse>("POST", `${flowBase(projectId)}/nodes/${nodeId}/keyframe`, {
      source_node_id: sourceNodeId,
    }),
  )
}

export async function purgeFlowEdge(projectId: number, edgeId: number): Promise<void> {
  await sendJson("POST", `${flowBase(projectId)}/edges/${edgeId}/purge`)
}

export async function purgeDiscardedFlowNodes(projectId: number, flowId: number): Promise<void> {
  await sendJson("POST", `${flowBase(projectId)}/boards/${flowId}/purge_deleted`)
}

export type FlowNodeVersionRow = {
  id: number
  active: boolean
  preview_url: string | null
  display_url?: string | null
  download_url?: string | null
  poster_url?: string | null
  origin?: string | null
  created_at: string
}

export async function listFlowNodeVersions(projectId: number, nodeId: number): Promise<FlowNodeVersionRow[]> {
  const data = await getJson<{ versions: FlowNodeVersionRow[] }>(`${flowBase(projectId)}/nodes/${nodeId}/generations`)
  return data.versions ?? []
}

export async function activateFlowNodeVersion(
  projectId: number,
  nodeId: number,
  versionId: number,
): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>(
    "PATCH",
    `${flowBase(projectId)}/nodes/${nodeId}/generations/${versionId}/activate`,
  )
  return data.node
}

export async function unlinkFlowNodeVersion(
  projectId: number,
  nodeId: number,
  versionId: number,
): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>(
    "DELETE",
    `${flowBase(projectId)}/nodes/${nodeId}/generations/${versionId}`,
  )
  return data.node
}

export type FlowShotPlayback = {
  timeline: TimelineDocument
  is_default: boolean
  primary_version_id: number
  aspect_ratio: number | null
  frame_rate: number
  sources: Record<string, TimelineSource>
  audio_assets: TimelineAudioAsset[]
  animatic: TimelineAnimaticSource | null
}

export async function fetchFlowNodePlayback(projectId: number, nodeId: number): Promise<FlowShotPlayback | null> {
  const data = await getJson<{ playback: FlowShotPlayback | null }>(`${flowBase(projectId)}/nodes/${nodeId}/playback`)
  return data.playback
}

export type FlowBulkQuote = {
  counts: Partial<Record<FlowNodeKind, number>>
  estimable: boolean
  total_microcents: number | null
  /** Валюта счёта списания проекта (docs/billing_currency_accounts.md). */
  currency: string
}

export type FlowBulkConsentOwner = {
  type: string
  id: number
  label: string
}

export type FlowBulkStart = {
  needs_consent?: boolean
  shot_ids?: number[]
  owners?: FlowBulkConsentOwner[]
  agreement_key?: string
  nodes?: FlowNodeRow[]
}

export async function quoteFlowBulkRegenerate(
  projectId: number,
  flowId: number,
  nodeIds: number[],
): Promise<FlowBulkQuote> {
  return sendJson<FlowBulkQuote>("POST", `${flowBase(projectId)}/boards/${flowId}/bulk_regenerate_quote`, {
    node_ids: nodeIds,
  })
}

export async function startFlowBulkRegenerate(
  projectId: number,
  flowId: number,
  nodeIds: number[],
  agreementVersion?: number,
): Promise<FlowBulkStart> {
  return sendJson<FlowBulkStart>("POST", `${flowBase(projectId)}/boards/${flowId}/bulk_regenerate`, {
    node_ids: nodeIds,
    ...(agreementVersion ? { agreement_version: agreementVersion } : {}),
  })
}

export async function generateFlowNode(
  projectId: number,
  nodeId: number,
  input: {
    prompt_text: string
    prompt_doc: JSONContent | null
    model_id: string
    aspect_ratio: string
    duration_sec: number | null
    model_params: Record<string, string>
    use_project_styles: boolean
  },
): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>("POST", `${flowBase(projectId)}/nodes/${nodeId}/generate`, input)
  return data.node
}

export type ShotFlowBoardPayload = {
  flow: (Omit<FlowBoardRow, "number"> & { number: null }) | null
  focus_node_id: number | null
  nodes: FlowNodeRow[]
  flow_models: CanvasModel[]
  flow_upscale_models: PostProductionModelOption[]
  flow_defaults: FlowDefaults
  flow_project_styles: CanvasProjectStyles
  project_cast: CastRow[]
  project_locations: LocationRow[]
  project_props: PropRow[]
}

export async function fetchShotFlowBoard(projectId: number, shotId: number): Promise<ShotFlowBoardPayload> {
  return getJson<ShotFlowBoardPayload>(`${flowBase(projectId)}/shots/${shotId}`)
}

export async function depthFlowNode(
  projectId: number,
  nodeId: number,
  input: { variant: "gray" | "vis" },
): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>("POST", `${flowBase(projectId)}/nodes/${nodeId}/depth`, {
    variant: input.variant,
  })
  return data.node
}

export async function upscaleFlowNode(
  projectId: number,
  nodeId: number,
  input: { model_id: string; parameters: PostProductionParameters },
): Promise<FlowNodeRow> {
  const data = await sendJson<{ node: FlowNodeRow }>("POST", `${flowBase(projectId)}/nodes/${nodeId}/upscale`, {
    model_id: input.model_id,
    parameters: input.parameters,
  })
  return data.node
}
